import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { Dropdown, IDropdownOption, TooltipHost, Stack, IStackTokens, Spinner, SpinnerSize, FontIcon, ComboBox, IComboBox, IComboBoxOption, IRenderFunction, TooltipOverflowMode, IDropdownStyles } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { RootState } from "../../redux";
import { DataSourcePanel } from "./datasourcePanel";
import { DataSourceDropdownInitializer } from "../helpers/datasourceDropdownInitializer";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

const dropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { maxWidth: 301 },
  dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
  dropdownItem: { height: 'auto' },
};

const onRenderTitle = (options: IDropdownOption[], defaultRender?: IRenderFunction<IDropdownOption[]>) => {
  const option: IDropdownOption = Array.isArray(options) && options.length > 0 && options[0];
  return <TooltipHost content={option?.text} overflowMode={TooltipOverflowMode.Parent}>
    {defaultRender(options)}
  </TooltipHost>
}

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: string): IDropdownOption[] => {
  const options = Array.isArray(defaultOptions) ? defaultOptions : [];
  if (allowFreeForm && inputValue) {
    if (!options.some(option => option.key && ((option.key as string).trim().toLowerCase() === inputValue.trim().toLowerCase()))) {
      options.push({ key: inputValue, text: inputValue });
    }
  }
  return options;
}

const mapDispatchToProps = {
  setDatasourceValues,
};

const mapStateToProps = (state: RootState) => {
  return {
    datasourceItems: state.tenantInfo.datasources,
    currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
  };
};

type reduxDispatch = typeof mapDispatchToProps;
type reduxState = ReturnType<typeof mapStateToProps>;
interface DatasourceDropdownReduxProps extends InputProps, reduxDispatch, reduxState { }

export const UnconnectedDataSourceDropdown: React.FunctionComponent<DatasourceDropdownReduxProps> = ({
  displayName,
  isRequired,
  inputValue,
  inputValidator,
  updateInputValidationStatus,
  error,
  datasourceItems,
  setDatasourceValues,
  currentDirectory,
  containerDisplayNames
}: DatasourceDropdownReduxProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [datasources, setDatasources] = React.useState<IDropdownOption[]>([]);
  const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
  const [boundedField, setBoundedField] = React.useState<string>("");
  const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
  const dataSourceService = new DatasourceService();
  const dataSourceHelper = new DatasourceHelper(dataSourceService);
  const [dataSourceItem] = React.useState(
    DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""])
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [enableAutoFillRelatedFields, setEnableAutoFillRelatedFields] = React.useState<boolean>(false);
  const [isMasterDatasource] = React.useState<boolean>(dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.isMaster);
  const [allowCustomValues] = React.useState<boolean>(dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.allowCustomValues);

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    if (!isFirstRun.current) {
      try {
        updateInputValidationStatus(inputValidator.validate(inputValue?.value));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (updatedFromDataSourceToggle)");
      }
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  React.useEffect(() => {
    if (!isFirstRun.current) {
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, false);
        dropdownInitializer.initialize((options) => setDatasources(getOptions(allowCustomValues, options, inputValue?.value)));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (resetDatasourceOptionsToggle)");
      }
    }
  }, [inputValue?.resetDatasourceOptionsToggle]);

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
    }

    const setupAsyncWrapper = async () => {
      setIsLoading(true);
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, false);
        // const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize(setDatasources)]);
        const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize((options) => setDatasources(prevOptions => prevOptions?.length === 0 ? getOptions(allowCustomValues, options, inputValue?.value) : prevOptions))]);
        if (Array.isArray(result)) {
          if (result.length > 1) setEnableAutoFillRelatedFields(true);
          setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
        }
        const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, boundName);
        setBoundedField(boundedFieldName ?? "");

        if (inputValue?.value && boundedFieldName) {
          getRelatedDatasourceValues(inputValue.value, boundedFieldName, false, true);
        }
      } catch (error) {
        logger(error, "dataSourceDropdown.tsx (useEffect 1)");
      }
      setIsLoading(false);
    };

    setupAsyncWrapper();
  }, []);

  React.useEffect(() => {
    if (Array.isArray(inputValue?.updatedDatasourceOptions)) {
      try {
        setDatasources(inputValue.updatedDatasourceOptions.map(option => ({ key: option, text: option })));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (updatedDatasourceOptions)");
      }
    }
  }, [inputValue?.updatedDatasourceOptions]);

  const closePanel = () => {
    setIsOpen(false);
  };

  const updateInputValue = (newValue: string) => {
    updateInputValidationStatus(inputValidator.validate([newValue]));
    updateSearch(newValue);
  };

  const updateSearchInputs = (index: number, value: string) => {
    DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
  };

  const openPanel = async () => {
    updateSearch(inputValue?.value);
    setIsOpen(true);
  };

  const updateSearch = (newValue: string) => {
    const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
    updateSearchInputs(index, newValue);
  };

  const onChangeHandle = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    updateSearch(item.key as string);
    updateInputValidationStatus(inputValidator.validate(item.key));
    if (enableAutoFillRelatedFields) getRelatedDatasourceValues(item.key as string, boundedField, true);
  };

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let key = option?.key;
      const isFreeFormValue: boolean = !option && value?.length > 0;
      if (isFreeFormValue) {
        key = value;
        setDatasources(prevOptions => [...prevOptions, { key: key!, text: value }]);
      }
      updateSearch(key as string);
      updateInputValidationStatus(inputValidator.validate(key));
      if (!isFreeFormValue && enableAutoFillRelatedFields) getRelatedDatasourceValues(key as string, boundedField, true);
    },
    [inputValidator, boundedField, enableAutoFillRelatedFields]
  );

  const getRelatedDatasourceValues = async (searchValue: string, boundedField: string, isStrict: boolean, initialLoad: boolean = false) => {
    if (searchValue?.trim()) {
      try {
        const request: IDatasourceQueryDto = {
          id: dataSourceItem.id,
          name: dataSourceItem.name,
          tenantId: dataSourceItem.tenantId,
          typeId: dataSourceItem.type.id,
          typeName: dataSourceItem.type.name,
          queryParams: [{ name: boundedField, value: searchValue.trim() }],
          returnOnlyQueriedProperties: false,
          isStrict: isStrict,
          allowCache: dataSourceItem.allowCache
        };

        const result = await dataSourceService.Search(request);
        if (result.length === 1) {
          if (!initialLoad) {
            const propertyKeys = Object.keys(result[0]); //keys of the data source, not the sharepoint columns
            if (propertyKeys) {
              let resultItem: any = result[0];
              if (resultItem) {
                let boundObject = {};
                propertyKeys
                  .forEach((key) => {
                    if (resultItem[key] != null) {
                      const boundField = dataSourceItem?.boundFields?.find(field => {
                        return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                      }); //check if any of the data source fields have a mapping.
                      if (boundField) { //if a mapping exists
                        const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                        names
                          .filter(name => name.toLowerCase().trim() !== boundName.toLowerCase().trim())
                          .forEach(name => {
                            boundObject[name] = resultItem[key];
                          })
                      } else {
                        boundObject[key] = resultItem[key];
                      }
                    }
                  });

                setDatasourceValues(boundObject, false, isMasterDatasource ? dataSourceItem?.id : null); //redux.    
              }
            }
          }
        }
        else if (isMasterDatasource && result.length > 1) {
          let resultItem: any = result[0];
          const propertyKeys = resultItem && Object.keys(result[0]);
          if (propertyKeys) {
            const options: any[] = [];
            result.forEach(resultRow => {
              let boundObject = {};
              propertyKeys
                .forEach((key) => {
                  if (resultRow[key]) {
                    const boundField = dataSourceItem?.boundFields?.find(field => {
                      return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                    });
                    if (boundField) {
                      const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                      names
                        .filter(name => name.toLowerCase().trim() !== boundName.toLowerCase().trim())
                        .forEach(name => {
                          boundObject[name] = resultRow[key];
                        })
                    } else {
                      boundObject[key] = resultRow[key];
                    }
                  }
                });
              options.push(boundObject);
            });
            setDatasourceValues(options, initialLoad, dataSourceItem?.id);
          }
        }
      } catch (error) {
        logger(error, "dataSourceDropdown.tsx (getRelatedDatasourceValues)");
      }
    }
  }

  const input = allowCustomValues
    ?
    <ComboBox
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={inputValue ? inputValue?.value : undefined}
      label={displayName}
      placeholder={languageStrings.SelectOption}
      options={datasources}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={inputValue ? inputValue?.value : undefined}
      placeholder={languageStrings.SelectOption}
      onChange={onChangeHandle}
      onRenderTitle={onRenderTitle}
      options={datasources}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
      styles={dropdownStyles}
    // responsiveMode={ResponsiveMode.small}
    />;

  const stackTokens: IStackTokens = {
    childrenGap: 5
  };

  return (
    <>
      <Stack horizontal verticalAlign="start" tokens={stackTokens}>
        <Stack.Item grow={1}>{input}</Stack.Item>
        {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
          {isLoading
            ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
            : <div className="xb-search-icon-container">
              <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                <FontIcon iconName="Search" onClick={openPanel} />
              </TooltipHost>
            </div>}
        </Stack.Item>}
      </Stack>
      <DataSourcePanel
        displayName={dataSourceItem?.name}
        closePanel={closePanel}
        isOpen={isOpen}
        datasourceItem={dataSourceItem}
        datasourceService={dataSourceService}
        searchInputs={searchInputs}
        updateSearchInputs={updateSearchInputs}
        updateInputValue={updateInputValue}
        boundedField={boundedField}
      />
    </>
  );
};

export const DataSourceDropdown = connect(mapStateToProps, mapDispatchToProps)(UnconnectedDataSourceDropdown);
